/**
 * CLOUDWEB-21080 (§5): удерживаемый диплинк на диске, а не в переменной модуля.
 *
 * Раньше `app_url` из `notificationclick` лежал в переменной прямо в обработчике и ждал,
 * пока страница выйдет на передний план и заберёт его через REQUEST. Переменная не переживает
 * терминацию воркера, а хуже — сам REQUEST идёт через `postMessage` в `registration.active`,
 * а это поднимает воркер новым инстансом с пустой памятью. Отказ выглядит как штатное
 * «диплинка не было», хотя на самом деле воркер, который его держал, уже снесён. Поэтому
 * рекорд `{appUrl, at}` переезжает в IDB-стор `SW_DEVICE_STATE_DB` под ключом
 * `PENDING_DEEPLINK_KEY` — запись переживает терминацию, а её протухание по TTL решает та же
 * проблему, что раньше решало время жизни переменной.
 *
 * Контракт как у `omicronConfig.ts` и `claimPushId`: наружу не всплывает ни одна ошибка IDB.
 * Недоступный IndexedDB, сбой транзакции, битая запись — всё это даёт «диплинка нет», а не
 * исключение. Обработчик `notificationclick` не должен терять переход в приложение из-за сбоя
 * побочного хранилища.
 *
 * Подключение к `notificationclick`/REQUEST — в Task 9, этот модуль пока только заводится.
 */
import { ensureStore, PENDING_DEEPLINK_KEY, SW_DEVICE_STATE_DB } from 'serviceWorker/swIdbSchema';

/**
 * Столько диплинк ждёт, пока страница не выйдет на передний план и не заберёт его.
 * Число из §5: тот же порядок, что и окно ожидания foreground после правок.
 */
export const PENDING_DEEPLINK_TTL_MS = 60000;

/** Рекорд под `PENDING_DEEPLINK_KEY`: пишет и читает только воркер (см. `swIdbSchema.ts`). */
export interface PendingDeeplinkRecord {
    appUrl: string;
    at: number;
}

export type ClassifiedPendingDeeplink = { status: 'fresh'; appUrl: string } | { status: 'expired' } | { status: 'empty' };

/**
 * Чистая классификация рекорда по TTL — единственная часть модуля, которую покрывают тесты
 * (IDB в зависимостях для тестов нет, слой хранения проверяется вручную на стенде).
 *
 * Возраст, ровно равный TTL, считается протухшим: строгое `<`, та же семантика, что у маяка
 * присутствия в `push/appPresence.ts` — расхождение в двух проверках TTL одной фичи было бы
 * трудноуловимым багом на границе.
 */
export function classifyPendingDeeplink(record: PendingDeeplinkRecord | null | undefined, now: number): ClassifiedPendingDeeplink {
    // Битая запись (нет appUrl, appUrl не строка, пустая строка) — то же самое, что «диплинка нет».
    // Проверяем это до возраста: иначе запись без appUrl, но с недавним `at`, ушла бы в `fresh`
    // с `appUrl: undefined`.
    if (!record || typeof record.appUrl !== 'string' || record.appUrl.length === 0) {
        return { status: 'empty' };
    }

    const age = now - record.at;

    if (age < PENDING_DEEPLINK_TTL_MS) {
        return { status: 'fresh', appUrl: record.appUrl };
    }

    return { status: 'expired' };
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

/** Открытие базы с мемоизацией соединения — по образцу `omicronConfig.ts`. */
function openDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') {
        return Promise.resolve(null);
    }

    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = indexedDB.open(SW_DEVICE_STATE_DB.name, SW_DEVICE_STATE_DB.version);

            request.onupgradeneeded = () => {
                ensureStore(request.result, SW_DEVICE_STATE_DB);
            };

            request.onsuccess = () => {
                const db = request.result;

                // Не держим соединение, если схему апгрейдит другой контекст: иначе его open
                // зависнет на onblocked. Кэш сбрасываем — переоткроемся при следующем обращении.
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };

                resolve(db);
            };
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });

    return dbPromise;
}

/** Положить диплинк с меткой времени. Никогда не бросает — сбой IDB тихо проглатывается. */
export async function setPendingDeeplink(appUrl: string): Promise<void> {
    try {
        const db = await openDb();

        if (!db) {
            return;
        }

        await new Promise<void>((resolve) => {
            try {
                const record: PendingDeeplinkRecord = { appUrl, at: Date.now() };
                const tx = db.transaction(SW_DEVICE_STATE_DB.store, 'readwrite');

                tx.objectStore(SW_DEVICE_STATE_DB.store).put(record, PENDING_DEEPLINK_KEY);

                tx.oncomplete = () => resolve();
                tx.onerror = () => resolve();
                tx.onabort = () => resolve();
            } catch {
                resolve();
            }
        });
    } catch {
        // Контракт модуля: наружу не всплывает ни одна ошибка IDB.
    }
}

/**
 * Прочитать и классифицировать рекорд. Недоступный IDB, сбой транзакции или отсутствие
 * рекорда неотличимы друг от друга снаружи — во всех случаях `{status: 'empty'}`.
 */
export async function readPendingDeeplink(now: number): Promise<ClassifiedPendingDeeplink> {
    try {
        const db = await openDb();

        if (!db) {
            return classifyPendingDeeplink(null, now);
        }

        const record = await new Promise<PendingDeeplinkRecord | null>((resolve) => {
            try {
                const request = db
                    .transaction(SW_DEVICE_STATE_DB.store, 'readonly')
                    .objectStore(SW_DEVICE_STATE_DB.store)
                    .get(PENDING_DEEPLINK_KEY);

                request.onsuccess = () => resolve((request.result as PendingDeeplinkRecord | undefined) ?? null);
                request.onerror = () => resolve(null);
            } catch {
                resolve(null);
            }
        });

        return classifyPendingDeeplink(record, now);
    } catch {
        return classifyPendingDeeplink(null, now);
    }
}

/** Снять рекорд после отдачи странице. Никогда не бросает — сбой IDB тихо проглатывается. */
export async function clearPendingDeeplink(): Promise<void> {
    try {
        const db = await openDb();

        if (!db) {
            return;
        }

        await new Promise<void>((resolve) => {
            try {
                const tx = db.transaction(SW_DEVICE_STATE_DB.store, 'readwrite');

                tx.objectStore(SW_DEVICE_STATE_DB.store).delete(PENDING_DEEPLINK_KEY);

                tx.oncomplete = () => resolve();
                tx.onerror = () => resolve();
                tx.onabort = () => resolve();
            } catch {
                resolve();
            }
        });
    } catch {
        // Контракт модуля: наружу не всплывает ни одна ошибка IDB.
    }
}
